import { useEffect, useState } from "react";
import { db } from "../../../firebase/firestore";
import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc
} from "firebase/firestore";

const OFFER_PRESETS = [
  {
    key: "free_delivery",
    label: "FREE DELIVERY",
    values: {
      code: "FREEDEL",
      offerType: "free_delivery",
      appliesToCategory: "all",
      discount: "0",
      flatDiscount: "0",
      minOrder: "0",
      maxDiscount: "0",
      expiry: "",
      usageLimit: "0",
      perUserLimit: "1",
    },
  },
  {
    key: "combo",
    label: "COMBO SELLING",
    values: {
      code: "COMBO20",
      offerType: "percentage",
      appliesToCategory: "combo",
      discount: "20",
      flatDiscount: "0",
      minOrder: "0",
      maxDiscount: "0",
      expiry: "",
      usageLimit: "0",
      perUserLimit: "1",
    },
  },
  {
    key: "festival",
    label: "FESTIVAL FLASH",
    values: {
      code: "SURTI10",
      offerType: "percentage",
      appliesToCategory: "all",
      discount: "10",
      flatDiscount: "0",
      minOrder: "0",
      maxDiscount: "0",
      expiry: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      usageLimit: "0",
      perUserLimit: "1",
    },
  },
  {
    key: "high_value",
    label: "HIGH VALUE HOOK",
    values: {
      code: "KHAMAN100",
      offerType: "flat",
      appliesToCategory: "all",
      discount: "0",
      flatDiscount: "100",
      minOrder: "499",
      maxDiscount: "100",
      expiry: "",
      usageLimit: "0",
      perUserLimit: "1",
    },
  },
];

function parseOfferNumber(value, fallback = 0) {
  const cleaned = String(value ?? "")
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "")
    .trim();
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [usageStats, setUsageStats] = useState({});
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [status, setStatus] = useState("");

  const [form, setForm] = useState({
    code: "",
    offerType: "percentage",
    appliesToCategory: "all",
    discount: "",
    flatDiscount: "",
    minOrder: "",
    maxDiscount: "",
    expiry: "",
    usageLimit: "",
    perUserLimit: ""
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "offers"),
      (snap) => {
        setOffers(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => Number(b.createdAtMs || 0) - Number(a.createdAtMs || 0))
        );
        setLoadingOffers(false);
        setStatus("");
      },
      () => {
        setLoadingOffers(false);
        setStatus("Live offers feed failed. Please refresh.");
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "offerUsage"),
      (snap) => {
        const stats = {};

        snap.docs.forEach((d) => {
          const usage = d.data() || {};
          const offerId = String(usage.offerId || "").trim();
          if (!offerId) return;

          const usedCount = Number(usage.usedCount || 0);
          if (!stats[offerId]) {
            stats[offerId] = {
              usedCount: 0,
              usedPeople: 0,
            };
          }

          stats[offerId].usedCount += usedCount;
          if (usedCount > 0) {
            stats[offerId].usedPeople += 1;
          }
        });

        setUsageStats(stats);
        setLoadingUsage(false);
      },
      () => {
        setLoadingUsage(false);
        setStatus((prev) => prev || "Offer usage stats are temporarily unavailable.");
      }
    );

    return () => unsubscribe();
  }, []);

  // 🔥 ADD OFFER
  const addOffer = async () => {
    const code = String(form.code || "")
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, "")
      .slice(0, 24);
    const offerType = String(form.offerType || "percentage");
    const appliesToCategory = String(form.appliesToCategory || "all");
    const discount = parseOfferNumber(form.discount, 0);
    const flatDiscount = parseOfferNumber(form.flatDiscount, 0);
    const minOrder = parseOfferNumber(form.minOrder, 0);
    const maxDiscount = parseOfferNumber(form.maxDiscount, 0);
    const usageLimit = parseOfferNumber(form.usageLimit, 0);
    const perUserLimit = parseOfferNumber(form.perUserLimit, 1);

    if (!code) {
      setStatus("Please enter a valid coupon code.");
      return;
    }

    if (offerType === "percentage" && discount <= 0) {
      setStatus("Percentage offers need discount % greater than 0.");
      return;
    }

    if (offerType === "flat" && flatDiscount <= 0) {
      setStatus("Flat offers need amount off greater than 0.");
      return;
    }

    const payload = {
      ...form,
      code,
      offerType,
      appliesToCategory,
      discount: offerType === "percentage" ? discount : 0,
      flatDiscount: offerType === "flat" ? flatDiscount : 0,
      minOrder,
      maxDiscount,
      usageLimit,
      perUserLimit,
      freeDelivery: offerType === "free_delivery",
      usedCount: 0,
      active: true,
      createdAt: new Date().toISOString(),
      createdAtMs: Date.now(),
    };

    if (offerType === "free_delivery") {
      payload.discount = 0;
      payload.flatDiscount = 0;
      payload.maxDiscount = 0;
      payload.deliveryFree = true;
    }

    await addDoc(collection(db, "offers"), payload);
    setStatus(`Offer ${code} added successfully.`);

    resetForm();
  };

  // 🔥 DELETE
  const deleteOffer = async (id) => {
    await deleteDoc(doc(db, "offers", id));
  };

  // 🔥 TOGGLE ACTIVE
  const toggleActive = async (offer) => {
    await updateDoc(doc(db, "offers", offer.id), {
      active: !offer.active
    });
  };

  const resetForm = () => {
    setForm({
      code: "",
      offerType: "percentage",
      appliesToCategory: "all",
      discount: "",
      flatDiscount: "",
      minOrder: "",
      maxDiscount: "",
      expiry: "",
      usageLimit: "",
      perUserLimit: ""
    });
  };

  const applyPreset = (preset) => {
    setForm((prev) => ({ ...prev, ...preset.values }));
    setStatus(`Preset loaded: ${preset.values.code}`);
  };

  const getOfferLabel = (offer) => {
    const type = String(offer.offerType || "").toLowerCase();
    const discount = Number(offer.discount || 0);
    const flatDiscount = Number(offer.flatDiscount || 0);

    if (offer.freeDelivery || type === "free_delivery") return "FREE DELIVERY";
    if (type === "flat" || flatDiscount > 0) return `Rs ${flatDiscount || discount} OFF`;
    return `${discount || 0}% OFF`;
  };

  const totalCouponUses = offers.reduce((sum, offer) => {
    const tracked = Number(usageStats[offer.id]?.usedCount || 0);
    const legacy = Number(offer.usedCount || 0);
    return sum + Math.max(tracked, legacy);
  }, 0);

  const totalUsedPeople = offers.reduce((sum, offer) => {
    const trackedPeople = Number(usageStats[offer.id]?.usedPeople || 0);
    const trackedUsed = Number(usageStats[offer.id]?.usedCount || 0);
    const legacyUsed = Number(offer.usedCount || 0);
    const fallbackPeople = trackedPeople || (Math.max(trackedUsed, legacyUsed) > 0 ? 1 : 0);
    return sum + fallbackPeople;
  }, 0);
  const activeCoupons = offers.filter((offer) => offer.active !== false).length;

  return (
    <div className="offers-page">

      <h1>🎁 Offers & Coupons</h1>
      {status ? <p className="form-status">{status}</p> : null}
      {(loadingOffers || loadingUsage) ? <p>Loading coupon dashboard...</p> : null}

      <div className="offers-summary">
        <article className="card offers-stat-card">
          <h4>Active Coupons</h4>
          <p>{activeCoupons}</p>
        </article>
        <article className="card offers-stat-card">
          <h4>Used People</h4>
          <p>{totalUsedPeople}</p>
        </article>
        <article className="card offers-stat-card">
          <h4>Total Coupon Uses</h4>
          <p>{totalCouponUses}</p>
        </article>
      </div>

      <div className="offer-template-grid">
        {OFFER_PRESETS.map((preset) => (
          <button
            key={preset.key}
            type="button"
            className="offer-template-btn"
            onClick={() => applyPreset(preset)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* 🔥 FORM */}
      <div className="card form-card">

        <p className="offer-form-hint">
          Tip: Use preset buttons above, or fill manually. Example codes: FREEDEL, COMBO20, SURTI10, KHAMAN100.
        </p>

        <div className="offer-form-field">
          <label htmlFor="offer-code">Coupon code</label>
          <input
            id="offer-code"
            placeholder="Coupon Code (e.g. SURTI10)"
            title="Unique code customer enters at checkout"
            value={form.code}
            onChange={(e)=>setForm({...form,code:e.target.value.toUpperCase()})}
          />
        </div>

        <div className="offer-form-field">
          <label htmlFor="offer-type">Offer type</label>
          <select
            id="offer-type"
            title="Choose how discount is applied"
            value={form.offerType}
            onChange={(e)=>setForm({...form,offerType:e.target.value})}
          >
            <option value="percentage">Percentage Discount</option>
            <option value="flat">Flat Amount Off</option>
            <option value="free_delivery">Free Delivery</option>
          </select>
        </div>

        <div className="offer-form-field">
          <label htmlFor="offer-category">Applies to category</label>
          <select
            id="offer-category"
            title="Limit coupon to a category, or keep for all items"
            value={form.appliesToCategory}
            onChange={(e)=>setForm({...form,appliesToCategory:e.target.value})}
          >
            <option value="all">All Items</option>
            <option value="combo">Combo Items Only</option>
            <option value="snacks">Snacks Only</option>
            <option value="sweets">Sweets Only</option>
          </select>
        </div>

        {form.offerType === "percentage" ? (
          <div className="offer-form-field">
            <label htmlFor="offer-discount-percent">Discount percentage</label>
            <input
              id="offer-discount-percent"
              placeholder="Discount % (e.g. 10 or 20)"
              title="Percentage discount value"
              value={form.discount}
              onChange={(e)=>setForm({...form,discount:e.target.value})}
            />
          </div>
        ) : null}

        {form.offerType === "flat" ? (
          <div className="offer-form-field">
            <label htmlFor="offer-flat-discount">Flat discount amount</label>
            <input
              id="offer-flat-discount"
              placeholder="Flat Off Amount Rs (e.g. 100)"
              title="Fixed amount to deduct"
              value={form.flatDiscount}
              onChange={(e)=>setForm({...form,flatDiscount:e.target.value})}
            />
          </div>
        ) : null}

        <div className="offer-form-field">
          <label htmlFor="offer-min-order">Minimum order amount</label>
          <input
            id="offer-min-order"
            placeholder="Minimum Order Rs (0 = no minimum)"
            title="Minimum eligible subtotal for this coupon"
            value={form.minOrder}
            onChange={(e)=>setForm({...form,minOrder:e.target.value})}
          />
        </div>

        <div className="offer-form-field">
          <label htmlFor="offer-max-discount">Maximum discount cap</label>
          <input
            id="offer-max-discount"
            placeholder="Max Discount Rs (0 = no cap)"
            title="Maximum discount allowed for percentage coupon"
            value={form.maxDiscount}
            onChange={(e)=>setForm({...form,maxDiscount:e.target.value})}
          />
        </div>

        <div className="offer-form-field">
          <label htmlFor="offer-expiry">Expiry date</label>
          <input
            id="offer-expiry"
            type="date"
            title="Expiry date (optional)"
            value={form.expiry}
            onChange={(e)=>setForm({...form,expiry:e.target.value})}
          />
        </div>

        <div className="offer-form-field">
          <label htmlFor="offer-usage-limit">Total usage limit</label>
          <input
            id="offer-usage-limit"
            placeholder="Usage Limit (0 = unlimited)"
            title="Global coupon usage limit"
            value={form.usageLimit}
            onChange={(e)=>setForm({...form,usageLimit:e.target.value})}
          />
        </div>

        <div className="offer-form-field">
          <label htmlFor="offer-per-user-limit">Per-user limit</label>
          <input
            id="offer-per-user-limit"
            placeholder="Per User Limit (e.g. 1)"
            title="How many times one account can use coupon"
            value={form.perUserLimit}
            onChange={(e)=>setForm({...form,perUserLimit:e.target.value})}
          />
        </div>

        <button onClick={addOffer}>Add Offer</button>

      </div>

      {/* 🔥 OFFERS LIST */}
      <div className="table-container">

        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Type</th>
              <th>Applies</th>
              <th>Discount</th>
              <th>Min</th>
              <th>Max</th>
              <th>Expiry</th>
              <th>Used People</th>
              <th>Used Count</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {offers.map(o => {

              const expired = o.expiry && new Date(o.expiry) < new Date();
              const usageLimit = Number(o.usageLimit || 0);
              const trackedUsedCount = Number(usageStats[o.id]?.usedCount || 0);
              const usedCount = Math.max(Number(o.usedCount || 0), trackedUsedCount);
              const usedPeople = Number(usageStats[o.id]?.usedPeople || 0);
              const usageReached = usageLimit > 0 && usedCount >= usageLimit;

              return (
                <tr key={o.id}>

                  <td>{o.code}</td>

                  <td>{String(o.offerType || (o.freeDelivery ? "free_delivery" : "percentage")).replace("_", " ")}</td>

                  <td>{o.appliesToCategory || "all"}</td>

                  <td>{getOfferLabel(o)}</td>

                  <td>₹{o.minOrder}</td>

                  <td>₹{o.maxDiscount}</td>

                  <td>{o.expiry || "-"}</td>

                  <td>{usedPeople}</td>

                  <td>{usedCount}{usageLimit > 0 ? ` / ${usageLimit}` : ""}</td>

                  <td>
                    {expired ? (
                      <span className="expired">Expired</span>
                    ) : usageReached ? (
                      <span className="inactive">Limit Reached</span>
                    ) : o.active ? (
                      <span className="active">Active</span>
                    ) : (
                      <span className="inactive">Inactive</span>
                    )}
                  </td>

                  <td>
                    <button onClick={()=>toggleActive(o)}>
                      {o.active ? "Disable" : "Enable"}
                    </button>

                    <button onClick={()=>deleteOffer(o.id)}>
                      Delete
                    </button>
                  </td>

                </tr>
              );
            })}
          </tbody>
        </table>

      </div>

    </div>
  );
}